import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';

export interface HighScore {
  id: string;
  quizId: string;
  playerName: string;
  score: number;
  totalQuestions: number;
  accuracy: number;
  timeMs: number;
  timestamp: Date;
}

export function useHighScores(quizId: string | null, limitCount: number = 10) {
  const [highScores, setHighScores] = useState<HighScore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!quizId) return;

    const fetchHighScores = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const highScoresRef = collection(db, 'highScores');
        const q = query(
          highScoresRef,
          where('quizId', '==', quizId),
          orderBy('timeMs', 'asc'),
          orderBy('accuracy', 'desc'),
          limit(limitCount)
        );
        
        const querySnapshot = await getDocs(q);
        const scores: HighScore[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          scores.push({
            id: doc.id,
            quizId: data.quizId,
            playerName: data.playerName,
            score: data.score,
            totalQuestions: data.totalQuestions,
            accuracy: data.accuracy,
            timeMs: data.timeMs,
            timestamp: data.timestamp?.toDate() || new Date(),
          });
        });
        
        setHighScores(scores);
      } catch (err) {
        console.error('Error fetching high scores:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch high scores');
      } finally {
        setLoading(false);
      }
    };

    fetchHighScores();
  }, [quizId, limitCount]);

  return { highScores, loading, error };
}

export async function submitHighScore(
  quizId: string,
  playerName: string,
  score: number,
  totalQuestions: number,
  timeMs: number
): Promise<void> {
  const accuracy = (score / totalQuestions) * 100;
  
  try {
    await addDoc(collection(db, 'highScores'), {
      quizId,
      playerName,
      score,
      totalQuestions,
      accuracy,
      timeMs,
      timestamp: Timestamp.now(),
    });
  } catch (err) {
    console.error('Error submitting high score:', err);
    throw err;
  }
}
